import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ResellerSite } from '@tips-crm/shared';
import {
  createSite,
  deleteSite,
  fetchBrands,
  fetchSellers,
  fetchSites,
  fetchSiteWidgetToken,
} from '../../api/endpoints.js';
import { errorMessage } from '../../api/client.js';
import { PageHeader } from '../../components/PageHeader.js';
import { DataTable, type Column } from '../../components/DataTable.js';
import { Modal } from '../../components/Modal.js';
import { Loading, ErrorState } from '../../components/States.js';

const WIDGET_SRC = 'https://widget.databaseemailer.app/widget.js';

function buildSnippet(siteId: string, token: string): string {
  return `<script
  src="${WIDGET_SRC}"
  data-site-id="${siteId}"
  data-seller-token="${token}"
  async></script>`;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn btn--secondary btn--sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard may be unavailable */
        }
      }}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function WidgetSnippetModal({ site, onClose }: { site: ResellerSite; onClose: () => void }) {
  const tokenQ = useQuery({
    queryKey: ['site-widget-token', site.id],
    queryFn: () => fetchSiteWidgetToken(site.id),
  });

  return (
    <Modal open title={`Embed snippet — ${site.domain}`} onClose={onClose}>
      {tokenQ.isLoading && <Loading label="Generating widget token…" />}
      {tokenQ.isError && <ErrorState error={tokenQ.error} onRetry={() => tokenQ.refetch()} />}
      {tokenQ.data && (
        <div className="snippet-block">
          <p className="muted">
            Paste this snippet into the <code>&lt;head&gt;</code> of {site.domain}.
          </p>
          <pre className="code-block">{buildSnippet(site.id, tokenQ.data.token)}</pre>
          <div className="form__actions">
            <CopyButton text={buildSnippet(site.id, tokenQ.data.token)} />
          </div>
          <p className="muted small">
            Public key: <code>{tokenQ.data.widget_public_key}</code>
          </p>
        </div>
      )}
    </Modal>
  );
}

interface SiteForm {
  seller_id: string;
  brand_id: string;
  domain: string;
  allowed_origins: string;
}

const EMPTY: SiteForm = { seller_id: '', brand_id: '', domain: '', allowed_origins: '' };

export function SitesPage() {
  const qc = useQueryClient();
  const [sellerFilter, setSellerFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [snippetSite, setSnippetSite] = useState<ResellerSite | null>(null);
  const [form, setForm] = useState<SiteForm>(EMPTY);

  const sellersQ = useQuery({ queryKey: ['sellers'], queryFn: fetchSellers });
  const sitesQ = useQuery({
    queryKey: ['sites', sellerFilter],
    queryFn: () => fetchSites(sellerFilter || undefined),
  });
  const brandsQ = useQuery({
    queryKey: ['brands', form.seller_id],
    queryFn: () => fetchBrands(form.seller_id || undefined),
    enabled: open && Boolean(form.seller_id),
  });

  const sellerName = (id: string) => sellersQ.data?.find((s) => s.id === id)?.name ?? id;

  const createMutation = useMutation({
    mutationFn: () =>
      createSite({
        seller_id: form.seller_id,
        brand_id: form.brand_id,
        domain: form.domain,
        allowed_origins: form.allowed_origins
          .split(',')
          .map((o) => o.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] });
      setOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });

  function openCreate() {
    setForm({ ...EMPTY, seller_id: sellerFilter || sellersQ.data?.[0]?.id || '' });
    setOpen(true);
  }

  const columns = useMemo<Column<ResellerSite>[]>(
    () => [
      { key: 'domain', header: 'Domain', render: (s) => s.domain },
      { key: 'seller', header: 'Seller', render: (s) => sellerName(s.seller_id) },
      {
        key: 'status',
        header: 'Status',
        render: (s) => <span className={`badge badge--status-${s.status}`}>{s.status}</span>,
      },
      {
        key: 'origins',
        header: 'Allowed origins',
        render: (s) => (s.allowed_origins.length ? s.allowed_origins.join(', ') : '—'),
      },
      {
        key: 'actions',
        header: '',
        render: (s) => (
          <div className="row-actions" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setSnippetSite(s)}>
              Widget snippet
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => {
                if (confirm(`Delete site "${s.domain}"?`)) deleteMutation.mutate(s.id);
              }}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation, sellersQ.data],
  );

  function set<K extends keyof SiteForm>(key: K, value: SiteForm[K]) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  return (
    <div className="page">
      <PageHeader
        title="Sites"
        subtitle="Embeddable widget sites per seller."
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            New site
          </button>
        }
      />

      <div className="toolbar">
        <select
          className="field__input"
          value={sellerFilter}
          onChange={(e) => setSellerFilter(e.target.value)}
        >
          <option value="">All sellers</option>
          {sellersQ.data?.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {deleteMutation.isError && <div className="form-error">{errorMessage(deleteMutation.error)}</div>}

      <DataTable
        columns={columns}
        rows={sitesQ.data}
        rowKey={(s) => s.id}
        isLoading={sitesQ.isLoading}
        isError={sitesQ.isError}
        error={sitesQ.error}
        onRetry={() => sitesQ.refetch()}
        emptyMessage="No sites yet."
      />

      <Modal open={open} title="New site" onClose={() => setOpen(false)}>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <label className="field">
            <span className="field__label">Seller</span>
            <select
              className="field__input"
              value={form.seller_id}
              onChange={(e) => set('seller_id', e.target.value)}
              required
            >
              <option value="">Select…</option>
              {sellersQ.data?.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Brand</span>
            <select
              className="field__input"
              value={form.brand_id}
              onChange={(e) => set('brand_id', e.target.value)}
              required
              disabled={!form.seller_id}
            >
              <option value="">{form.seller_id ? 'Select…' : 'Pick a seller first'}</option>
              {brandsQ.data?.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Domain</span>
            <input
              className="field__input"
              placeholder="example.com"
              value={form.domain}
              onChange={(e) => set('domain', e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Allowed origins (comma-separated)</span>
            <input
              className="field__input"
              placeholder="https://example.com, https://www.example.com"
              value={form.allowed_origins}
              onChange={(e) => set('allowed_origins', e.target.value)}
            />
          </label>

          {createMutation.isError && <div className="form-error">{errorMessage(createMutation.error)}</div>}

          <div className="form__actions">
            <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={createMutation.isPending || !form.seller_id || !form.brand_id || !form.domain}
            >
              {createMutation.isPending ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {snippetSite && <WidgetSnippetModal site={snippetSite} onClose={() => setSnippetSite(null)} />}
    </div>
  );
}
